import SEO from './../components/SEO';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Pencil, Trash2, Save, X, RefreshCw,
  CheckCircle, AlertCircle, Users, Mail, Phone, MapPin,
} from 'lucide-react';
import { listClients, createClient, updateClient, deleteClient } from '../services/api';

// ── Shared style constants ───────────────────────────────────────────────────

const inputCls =
  'w-full px-3 py-2 text-sm rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-white ' +
  'border-gray-300 dark:border-gray-700 placeholder-gray-400 focus:outline-none ' +
  'focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-colors';

const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

const EMPTY_FORM = {
  client_name: '', client_email: '', client_phone: '',
  client_address: '', client_city: '', client_state: '', client_zip: '',
  client_country: '', client_gstin: '', notes: '',
};

// ── Toast ────────────────────────────────────────────────────────────────────

function Toast({ toast, onClose }) {
  if (!toast) return null;
  const ok = toast.type === 'success';
  return (
    <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium ${
      ok
        ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
        : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
    }`}>
      {ok
        ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
        : <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
      <span>{toast.message}</span>
      <button onClick={onClose} className="ml-1 opacity-60 hover:opacity-100 transition-opacity">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────────────────

function Field({ label, hint, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{hint}</p>}
    </div>
  );
}

// ── Client card (list view) ──────────────────────────────────────────────────

function ClientCard({ client, onEdit, onDelete, deleting }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const initials = (client.client_name || '?')
    .split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 flex gap-4 hover:shadow-md transition-shadow">
      {/* Avatar */}
      <div className="flex-shrink-0 w-11 h-11 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold text-sm select-none">
        {initials}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{client.client_name}</p>
        <div className="mt-1 space-y-0.5">
          {client.client_email && (
            <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 truncate">
              <Mail className="w-3 h-3 flex-shrink-0" /> {client.client_email}
            </p>
          )}
          {client.client_phone && (
            <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 truncate">
              <Phone className="w-3 h-3 flex-shrink-0" /> {client.client_phone}
            </p>
          )}
          {(client.client_city || client.client_country) && (
            <p className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 truncate">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              {[client.client_city, client.client_country].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
        {client.client_gstin && (
          <span className="mt-1.5 inline-block text-[10px] font-medium px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded">
            GST
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-1.5 flex-shrink-0">
        {confirmDelete ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs text-gray-600 dark:text-gray-400 text-right">Delete?</p>
            <div className="flex gap-1.5">
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 px-2.5 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setConfirmDelete(false); onDelete(client); }}
                disabled={deleting === client.id}
                className="flex-1 flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                {deleting === client.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Yes'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              onClick={() => onEdit(client)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={deleting === client.id}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Client form (create / edit) ──────────────────────────────────────────────

function ClientForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [formError, setFormError] = useState('');
  const nameRef = useRef(null);

  const set = (key, val) => {
    if (key === 'client_name' && nameError) setNameError('');
    setForm(f => ({ ...f, [key]: val }));
  };

  const handleSubmit = async () => {
    if (!form.client_name.trim()) {
      setNameError('Client name is required.');
      nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      nameRef.current?.querySelector('input')?.focus();
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await onSave(form);
    } catch (err) {
      setFormError(err.message || 'Failed to save client');
      setSaving(false);
    }
  };

  const isEdit = !!initial?.id;

  const SaveBtn = ({ className }) => (
    <button onClick={handleSubmit} disabled={saving} className={className}>
      {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
      {saving ? 'Saving…' : 'Save Client'}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              <span className="text-sm">Clients</span>
            </button>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700" />
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEdit ? 'Edit Client' : 'New Client'}
            </h1>
          </div>
          <SaveBtn className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:px-6 space-y-6">
        {formError && (
          <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-300">{formError}</p>
          </div>
        )}

        {/* Client Identity */}
        <div
          ref={nameRef}
          className={`bg-white dark:bg-gray-900 rounded-xl border p-6 space-y-4 transition-colors ${
            nameError ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-800'
          }`}
        >
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Client Identity</h2>
          <Field label="Client Name *">
            <input
              type="text"
              className={`${inputCls} ${nameError ? 'border-red-400 dark:border-red-600 focus:ring-red-500/40 focus:border-red-500' : ''}`}
              placeholder="e.g. Acme Corp"
              value={form.client_name}
              onChange={e => set('client_name', e.target.value)}
              autoFocus
            />
            {nameError && (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {nameError}
              </p>
            )}
          </Field>
        </div>

        {/* Contact Details */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Contact Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email">
              <input type="email" className={inputCls} placeholder="client@company.com" value={form.client_email} onChange={e => set('client_email', e.target.value)} />
            </Field>
            <Field label="Phone">
              <input type="tel" className={inputCls} placeholder="+1 555 000 0000" value={form.client_phone} onChange={e => set('client_phone', e.target.value)} />
            </Field>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Address</h2>
          <Field label="Street Address">
            <input type="text" className={inputCls} placeholder="123 Business Ave" value={form.client_address} onChange={e => set('client_address', e.target.value)} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="City">
              <input type="text" className={inputCls} placeholder="New York" value={form.client_city} onChange={e => set('client_city', e.target.value)} />
            </Field>
            <Field label="State / Province">
              <input type="text" className={inputCls} placeholder="NY" value={form.client_state} onChange={e => set('client_state', e.target.value)} />
            </Field>
            <Field label="ZIP / Postal">
              <input type="text" className={inputCls} placeholder="10001" value={form.client_zip} onChange={e => set('client_zip', e.target.value)} />
            </Field>
          </div>
          <Field label="Country">
            <input type="text" className={inputCls} placeholder="United States" value={form.client_country} onChange={e => set('client_country', e.target.value)} />
          </Field>
        </div>

        {/* Tax / Notes */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Tax &amp; Notes</h2>
          <Field label="GSTIN" hint="GST Identification Number (India)">
            <input type="text" className={inputCls} placeholder="22AAAAA0000A1Z5" value={form.client_gstin} onChange={e => set('client_gstin', e.target.value)} />
          </Field>
          <Field label="Notes" hint="Internal notes about this client (not shown on invoices)">
            <textarea
              className={`${inputCls} resize-none`}
              rows={3}
              placeholder="Payment terms, special requirements…"
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
            />
          </Field>
        </div>

        {/* Save footer */}
        <div className="flex justify-end pb-8">
          <SaveBtn className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors shadow-sm" />
        </div>
      </main>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | 'new' | client object
  const [deleting, setDeleting] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => {
    let active = true;
    listClients()
      .then(data => { if (active) setClients(data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const handleSave = useCallback(async (formData) => {
    if (editing === 'new') {
      const created = await createClient(formData);
      setClients(prev => [created, ...prev].sort((a, b) => a.client_name.localeCompare(b.client_name)));
      setEditing(null);
      showToast('Client saved!', 'success');
    } else {
      const updated = await updateClient(editing.id, formData);
      setClients(prev => prev.map(c => c.id === editing.id ? updated : c));
      setEditing(null);
      showToast('Client updated!', 'success');
    }
  }, [editing, showToast]);

  const handleDelete = useCallback(async (client) => {
    setDeleting(client.id);
    try {
      await deleteClient(client.id);
      setClients(prev => prev.filter(c => c.id !== client.id));
      showToast('Client deleted.', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete client', 'error');
    } finally {
      setDeleting(null);
    }
  }, [showToast]);

  const filtered = clients.filter(c =>
    !search.trim() ||
    c.client_name.toLowerCase().includes(search.toLowerCase()) ||
    (c.client_email || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.client_city || '').toLowerCase().includes(search.toLowerCase())
  );

  // ── Form view ──────────────────────────────────────────────────────────────
  if (editing !== null) {
    return (
      <ClientForm
        initial={editing === 'new' ? null : editing}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
      />
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO title="Clients | Invilic" description="Manage your client list on Invilic." canonical="https://www.invilic.com/clients" noindex={true} />
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline text-sm">Home</span>
            </Link>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700" />
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Client Address Book
            </h1>
          </div>
          <button
            onClick={() => setEditing('new')}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Client</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:px-6 space-y-5">
        {/* Info banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
          Saved clients appear as a quick-fill option when you create a new invoice. Pick one and your client details are filled in instantly.
        </div>

        {/* Search */}
        {clients.length > 0 && (
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email or city…"
              className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-colors"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
            </svg>
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-16">
            <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!loading && clients.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 text-center px-4">
            <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-blue-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No clients yet</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-xs">
              Add your clients once and reuse their details across all your invoices.
            </p>
            <button
              onClick={() => setEditing('new')}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" /> Add your first client
            </button>
          </div>
        )}

        {/* No search results */}
        {!loading && clients.length > 0 && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">No clients match "{search}"</p>
            <button onClick={() => setSearch('')} className="mt-2 text-sm text-blue-600 hover:underline">Clear search</button>
          </div>
        )}

        {/* Client list */}
        {!loading && filtered.length > 0 && (
          <div className="space-y-3">
            {filtered.map(client => (
              <ClientCard
                key={client.id}
                client={client}
                onEdit={c => setEditing(c)}
                onDelete={handleDelete}
                deleting={deleting}
              />
            ))}
            <button
              onClick={() => setEditing('new')}
              className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add another client
            </button>
          </div>
        )}
      </main>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
